import TagIcon from "@/components/TagIcon";
import { asText, isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

/**
 * @param {{ item: import("@prismicio/client").Content.ProjectProjectsSliceDefaultPrimaryProjectsItem }} props
 */
const RelatedProjectCard = ({ item }) => {
  const { project } = item;

  if (!isFilled.contentRelationship(project) || !project.data) {
    return null;
  }

  const { preview_image, preview_title, preview_description, type } = project.data;
  const name = isFilled.richText(preview_title) ? asText(preview_title) : "";
  const description = isFilled.richText(preview_description)
    ? asText(preview_description)
    : "";
  const heading = [name, description].filter(Boolean).join(" : ");

  if (!isFilled.image(preview_image) && !heading) {
    return null;
  }

  return (
    <PrismicNextLink
      prefetch={false}
      field={project}
      className="flex min-w-0 flex-1 flex-col items-start justify-center gap-5 overflow-clip"
    >
      {isFilled.image(preview_image) && (
        <div className="relative h-[288px] w-full overflow-hidden rounded-[10px]">
          <PrismicNextImage
            field={preview_image}
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 100vw, 378px"
          />
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-3">
        {type && <TagIcon tag={type} />}
        {heading && (
          <h3 className="p-lg-semibold tracking-[0.2px] text-bleu">{heading}</h3>
        )}
      </div>
    </PrismicNextLink>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ProjectProjectsSlice} ProjectProjectsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProjectProjectsSlice>} ProjectProjectsProps
 * @type {import("react").FC<ProjectProjectsProps>}
 */
const ProjectProjects = ({ slice }) => {
  const { title, projects } = slice.primary;
  const filledProjects = Array.isArray(projects)
    ? projects
        .filter(
          (item) =>
            isFilled.contentRelationship(item.project) &&
            Boolean(item.project.data),
        )
        .slice(0, 2)
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <div className="flex w-full flex-col items-start gap-[30px]">
        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading2: ({ children }) => (
                <h2 className="font-body text-[22px] font-medium leading-[26px] tracking-[0.2px] text-bleu">
                  {children}
                </h2>
              ),
              heading3: ({ children }) => (
                <h3 className="font-body text-[22px] font-medium leading-[26px] tracking-[0.2px] text-bleu">
                  {children}
                </h3>
              ),
            }}
          />
        )}

        {filledProjects.length > 0 && (
          <div className="flex w-full flex-col items-start gap-[28px] md:flex-row">
            {filledProjects.map((item, index) => (
              <RelatedProjectCard key={`project-${index}`} item={item} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectProjects;
